import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Loader2, Plus, ExternalLink, RefreshCw, AlertTriangle, FileText, Check, X, Factory, Play } from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney, formatDateTime, formatDate } from '@/lib/api'
import { MENU_ESTILOS, getMenuEstilo, setMenuEstilo, getZoom, setZoom as persistZoom, DASH_ESTILOS, getDashEstilo, setDashEstilo, type MenuEstilo, type DashEstilo } from '@/lib/aparencia'
import WhatsAppCard from '@/components/WhatsAppCard'
import { useFilial } from '@/lib/filial'

// ============================================================
// ESTOQUE
// ============================================================
export function EstoquePage() {
  const filialAtualId = useFilial(s => s.filialAtual?.id ?? null)
  const { data: alertas = [] } = useQuery<any[]>({
    queryKey: ['estoque-alertas'],
    queryFn: async () => (await api.get('/estoque/alerta-minimo')).data,
  })
  const { data: movs = [], isLoading } = useQuery<any[]>({
    queryKey: ['estoque-movs', filialAtualId],
    queryFn: async () => (await api.get('/estoque/movimentos')).data,
  })

  const [filtroData, setFiltroData] = useState<'hoje' | '7d' | '30d' | 'mes' | 'tudo' | 'custom'>('mes')
  const [dataDe, setDataDe] = useState('')
  const [dataAte, setDataAte] = useState('')
  const [filtroTipo, setFiltroTipo] = useState<'todos' | 'entrada' | 'saida' | 'ajuste'>('todos')

  const movsFiltrados = useMemo(() => {
    let list = movs
    if (filtroTipo !== 'todos') list = list.filter((m: any) => m.tipo === filtroTipo)
    if (filtroData !== 'tudo') {
      const agora = new Date()
      let dtInicio: Date | null = null
      let dtFim: Date | null = null
      if (filtroData === 'hoje') {
        dtInicio = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate())
        dtFim = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() + 1)
      } else if (filtroData === '7d') {
        dtInicio = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - 6)
        dtFim = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() + 1)
      } else if (filtroData === '30d') {
        dtInicio = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() - 29)
        dtFim = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate() + 1)
      } else if (filtroData === 'mes') {
        dtInicio = new Date(agora.getFullYear(), agora.getMonth(), 1)
        dtFim = new Date(agora.getFullYear(), agora.getMonth() + 1, 1)
      } else if (filtroData === 'custom') {
        if (dataDe) dtInicio = new Date(dataDe + 'T00:00:00')
        if (dataAte) {
          const d = new Date(dataAte + 'T00:00:00')
          dtFim = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
        }
      }
      if (dtInicio || dtFim) {
        list = list.filter((m: any) => {
          if (!m.criado_em) return false
          const dp = new Date(m.criado_em)
          if (dtInicio && dp < dtInicio) return false
          if (dtFim && dp >= dtFim) return false
          return true
        })
      }
    }
    return list
  }, [movs, filtroTipo, filtroData, dataDe, dataAte])

  return (
    <div className="space-y-4">
      {alertas.length > 0 && (
        <div className="card card-body bg-amber-50 border-amber-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-600 shrink-0" size={20} />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900">Produtos abaixo do estoque mínimo ({alertas.length})</h3>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {alertas.slice(0, 9).map((p: any) => (
                  <div key={p.id} className="text-sm bg-white rounded px-3 py-1.5 border border-amber-200">
                    <span className="font-medium">{p.descricao}</span>
                    <span className="text-amber-700 ml-2">({p.estoque_atual}/{p.estoque_minimo})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="px-5 py-4 border-b border-ink-200 flex flex-wrap items-center gap-3">
          <h3 className="font-semibold">Últimas Movimentações</h3>
          <span className="text-xs text-ink-500">({movsFiltrados.length} de {movs.length})</span>
          <div className="flex items-center gap-1 bg-ink-50 rounded-md p-1 ml-auto">
            {([
              ['hoje', 'Hoje'],
              ['7d', '7d'],
              ['30d', '30d'],
              ['mes', 'Mês'],
              ['tudo', 'Tudo'],
              ['custom', 'Personalizado'],
            ] as const).map(([v, label]) => (
              <button
                key={v}
                onClick={() => setFiltroData(v)}
                className={`px-2 py-1 text-xs rounded transition ${
                  filtroData === v
                    ? 'bg-white text-brand-700 font-medium shadow-sm'
                    : 'text-ink-600 hover:text-ink-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {filtroData === 'custom' && (
            <div className="flex items-center gap-1 text-xs">
              <input type="date" className="input input-sm" value={dataDe}
                onChange={e => setDataDe(e.target.value)} />
              <span className="text-ink-500">até</span>
              <input type="date" className="input input-sm" value={dataAte}
                onChange={e => setDataAte(e.target.value)} />
            </div>
          )}
          <select
            className="input input-sm w-32"
            value={filtroTipo}
            onChange={e => setFiltroTipo(e.target.value as any)}
          >
            <option value="todos">Todos tipos</option>
            <option value="entrada">Entradas</option>
            <option value="saida">Saídas</option>
            <option value="ajuste">Ajustes</option>
          </select>
        </div>
        <div className="table-wrap !rounded-t-none !border-t-0">
          <table className="table">
            <thead><tr><th>Data</th><th>Produto</th><th>Tipo</th><th>Origem</th><th className="text-right">Qtd</th><th className="text-right">Saldo</th></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="text-center py-8 text-ink-500">Carregando...</td></tr>}
              {!isLoading && movsFiltrados.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-ink-500">Nenhuma movimentação no período/filtro selecionado.</td></tr>
              )}
              {movsFiltrados.map((m: any) => (
                <tr key={m.id}>
                  <td className="text-ink-600">{formatDateTime(m.criado_em)}</td>
                  <td>Produto #{m.produto_id}</td>
                  <td><span className={m.tipo === 'entrada' ? 'badge-success' : m.tipo === 'saida' ? 'badge-danger' : 'badge-neutral'}>{m.tipo}</span></td>
                  <td className="capitalize text-ink-600">{m.origem}</td>
                  <td className="text-right font-medium">{Number(m.quantidade).toLocaleString('pt-BR')}</td>
                  <td className="text-right">{Number(m.saldo_pos).toLocaleString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// (FinanceiroPage moved to FinanceiroPage.tsx)

// ============================================================
// FISCAL
// ============================================================
export function FiscalPage() {
  const { data: docs = [], isLoading } = useQuery<any[]>({
    queryKey: ['fiscal-docs'],
    queryFn: async () => (await api.get('/fiscal/documentos')).data,
  })

  return (
    <div className="space-y-4">
      <div className="card card-body bg-brand-50 border-brand-200">
        <div className="flex items-start gap-3">
          <FileText className="text-brand-600 shrink-0" size={20} />
          <div className="text-sm text-brand-900">
            As notas são emitidas automaticamente via API (Focus NFe / PlugNotas) quando você emite uma NFC-e a partir de um pedido confirmado.
            Configure o token em <code className="bg-white px-1 rounded">.env</code>.
          </div>
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead><tr>
            <th>Data</th><th>Tipo</th><th>Pedido</th><th>Número</th>
            <th>Chave</th><th className="text-right">Valor</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="text-center py-8">Carregando...</td></tr>}
            {!isLoading && docs.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-ink-500">Nenhum documento emitido.</td></tr>}
            {docs.map(d => (
              <tr key={d.id}>
                <td className="text-ink-600">{formatDateTime(d.criado_em)}</td>
                <td><span className="badge-info">{d.tipo}</span></td>
                <td>#{d.pedido_id}</td>
                <td className="font-mono text-xs">{d.numero || '-'}</td>
                <td className="font-mono text-xs truncate max-w-xs">{d.chave || '-'}</td>
                <td className="text-right">{formatMoney(d.valor_total)}</td>
                <td><span className={
                  d.status === 'autorizado' ? 'badge-success' :
                  d.status === 'rejeitado' ? 'badge-danger' :
                  d.status === 'cancelado' ? 'badge-neutral' : 'badge-warning'
                }>{d.status}</span></td>
                <td>
                  {d.pdf_url && <a href={d.pdf_url} target="_blank" rel="noreferrer"
                    className="p-1.5 inline-flex rounded hover:bg-ink-100 text-ink-600"><ExternalLink size={14} /></a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ============================================================
// MARKETPLACE
// ============================================================
export function MarketplacePage() {
  const qc = useQueryClient()
  const { data: mlStatus } = useQuery<any>({
    queryKey: ['ml-status'],
    queryFn: async () => (await api.get('/ml/status')).data,
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
  })
  const { data: pedidos = [] } = useQuery<any[]>({
    queryKey: ['mkt-pedidos'],
    queryFn: async () => (await api.get('/marketplace/pedidos')).data,
  })

  const sincronizar = useMutation({
    mutationFn: async () => (await api.post('/ml/sincronizar-pedidos')).data,
    onSuccess: (r) => {
      const a = r.anuncios || {}
      const partes = [
        `${r.criados}/${r.total_recebidos} pedidos`,
        a.total_anuncios != null
          ? `${a.produtos_criados} novos produtos (de ${a.total_anuncios} anúncios)`
          : null,
        r.pulados_sem_mapeamento ? `${r.pulados_sem_mapeamento} pulados` : null,
      ].filter(Boolean)
      toast.success(partes.join(' · '))
      qc.invalidateQueries({ queryKey: ['mkt-pedidos'] })
    },
    onError: (e: any) => {
      toast.error(e?.response?.data?.detail || 'Falha ao sincronizar')
    },
  })

  async function conectarML() {
    try {
      const { data } = await api.get('/ml/conectar')
      if (data.url_autorizacao) window.location.href = data.url_autorizacao
    } catch (e: any) {
      toast.error(e?.response?.data?.detail?.mensagem || e?.response?.data?.detail || 'Configure App ID e Client Secret em Configurações > Empresa primeiro')
    }
  }

  const conectado = mlStatus?.conectado

  return (
    <div className="space-y-4">
      <div className="card card-body">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Mercado Livre</h3>
            <p className="text-sm text-ink-500 mt-1">
              {conectado
                ? `Conectado como ${mlStatus.ml_nickname} (${mlStatus.ml_country} · user ${mlStatus.ml_user_id})`
                : 'Conecte para importar pedidos automaticamente.'}
            </p>
          </div>
          {conectado ? (
            <button onClick={() => sincronizar.mutate()} disabled={sincronizar.isPending} className="btn-primary">
              <RefreshCw size={16} className={sincronizar.isPending ? 'animate-spin' : ''} />
              Sincronizar pedidos
            </button>
          ) : (
            <button onClick={conectarML} className="btn-primary">
              <Plus size={16} /> Conectar Mercado Livre
            </button>
          )}
        </div>
      </div>

      <div className="card">
        <div className="px-5 py-4 border-b border-ink-200"><h3 className="font-semibold">Pedidos Recebidos</h3></div>
        <div className="table-wrap !rounded-t-none !border-t-0">
          <table className="table">
            <thead><tr><th>Data</th><th>Código Externo</th><th>Status</th><th className="text-right">Valor</th><th>Pedido Interno</th></tr></thead>
            <tbody>
              {pedidos.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-ink-500">Nenhum pedido de marketplace.</td></tr>}
              {pedidos.map(p => (
                <tr key={p.id}>
                  <td className="text-ink-600">{formatDateTime(p.recebido_em)}</td>
                  <td className="font-mono text-xs">{p.codigo_externo}</td>
                  <td><span className="badge-info">{p.status_externo}</span></td>
                  <td className="text-right font-medium">{formatMoney(p.valor_total)}</td>
                  <td>{p.pedido_id ? `#${p.pedido_id}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// OrdensProducaoPage movida para pages/OrdensProducao.tsx (IND-3)

// ============================================================
// FICHAS TÉCNICAS
// ============================================================
export function FichasTecnicasPage() {
  const { data: fichas = [], isLoading } = useQuery<any[]>({
    queryKey: ['fichas'],
    queryFn: async () => (await api.get('/industrial/fichas')).data,
  })

  return (
    <div className="table-wrap">
      <table className="table">
        <thead><tr><th>Produto</th><th>Descrição</th><th className="text-right">Tempo (min)</th><th className="text-right">Itens</th></tr></thead>
        <tbody>
          {isLoading && <tr><td colSpan={4} className="text-center py-8">Carregando...</td></tr>}
          {!isLoading && fichas.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-ink-500">Nenhuma ficha técnica.</td></tr>}
          {fichas.map(f => (
            <tr key={f.id}>
              <td>Produto #{f.produto_id}</td>
              <td>{f.descricao || '-'}</td>
              <td className="text-right">{f.tempo_producao_min}</td>
              <td className="text-right">{f.itens?.length || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ============================================================
// CATEGORIAS
// ============================================================
export function CategoriasPage() {
  const qc = useQueryClient()
  const [nome, setNome] = useState('')
  const { data: cats = [] } = useQuery<any[]>({
    queryKey: ['categorias'],
    queryFn: async () => (await api.get('/cadastros/categorias')).data,
  })
  const criar = useMutation({
    mutationFn: async () => (await api.post('/cadastros/categorias', { nome, ativo: true })).data,
    onSuccess: () => { toast.success('Categoria criada'); setNome(''); qc.invalidateQueries({ queryKey: ['categorias'] }) },
  })
  return (
    <div className="space-y-4 max-w-2xl">
      <div className="card card-body">
        <div className="flex gap-2">
          <input className="input flex-1" placeholder="Nova categoria..." value={nome} onChange={e => setNome(e.target.value)} />
          <button disabled={!nome || criar.isPending} onClick={() => criar.mutate()} className="btn-primary">
            <Plus size={16} /> Adicionar
          </button>
        </div>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Nome</th><th>Status</th></tr></thead>
          <tbody>
            {cats.length === 0 && <tr><td colSpan={2} className="text-center py-8 text-ink-500">Nenhuma categoria.</td></tr>}
            {cats.map(c => (
              <tr key={c.id}>
                <td className="font-medium">{c.nome}</td>
                <td>{c.ativo ? <span className="badge-success">Ativa</span> : <span className="badge-neutral">Inativa</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ============================================================
// CONFIGURAÇÕES
// ============================================================
function AparenciaCard() {
  const [estilo, setEstilo] = useState<MenuEstilo>(() => getMenuEstilo())
  const [dash, setDash] = useState<DashEstilo>(() => getDashEstilo())
  const [zoom, setZoomState] = useState<number>(() => getZoom())

  const DashMini = ({ id }: { id: DashEstilo }) => (
    <div className="w-full h-20 rounded-lg border border-ink-200 bg-ink-50 overflow-hidden p-1.5 flex flex-col gap-1">
      {id === 'executivo' ? (
        <>
          <div className="h-9 rounded-md bg-gradient-to-br from-brand-500 to-violet-600 flex items-center gap-1 px-1.5">
            {[0,1,2,3].map(i => <div key={i} className="flex-1 h-5 rounded bg-white/25" />)}
          </div>
          <div className="flex-1 rounded bg-white border border-ink-200" />
        </>
      ) : id === 'compacto' ? (
        <div className="grid grid-cols-4 grid-rows-2 gap-1 h-full">
          {Array.from({length:8}).map((_,i) => <div key={i} className="rounded bg-white border border-ink-200" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-1 h-7">
            {[0,1,2,3].map(i => <div key={i} className="rounded bg-white border border-ink-200" />)}
          </div>
          <div className="flex-1 rounded bg-white border border-ink-200" />
        </>
      )}
    </div>
  )

  const Mini = ({ id }: { id: MenuEstilo }) => (
    <div className="w-full h-20 rounded-lg border border-ink-200 bg-ink-50 overflow-hidden flex">
      {id === 'topo' ? (
        <div className="flex-1 flex flex-col">
          <div className="h-4 bg-brand-600/80 flex items-center gap-0.5 px-1">
            {[0,1,2,3].map(i => <div key={i} className="h-1 w-3 rounded-full bg-white/70" />)}
          </div>
          <div className="flex-1 m-1.5 rounded bg-white border border-ink-200" />
        </div>
      ) : (
        <>
          <div className={`bg-brand-600/80 flex flex-col gap-1 p-1 ${id === 'flutuante' ? 'm-1.5 w-7 rounded-lg' : 'w-7'}`}>
            {[0,1,2].map(i => <div key={i} className="h-1.5 rounded-full bg-white/70" />)}
          </div>
          <div className="flex-1 m-1.5 rounded bg-white border border-ink-200" />
        </>
      )}
    </div>
  )

  return (
    <div className="card">
      <div className="px-5 py-4 border-b border-ink-200">
        <h3 className="font-semibold">Aparência</h3>
        <p className="text-xs text-ink-500 mt-0.5">Preferências deste navegador — estilo do menu e zoom da tela.</p>
      </div>
      <div className="p-5 space-y-5">
        <div>
          <div className="text-sm font-medium text-ink-700 mb-2">Estilo do menu</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {MENU_ESTILOS.map(m => (
              <button key={m.id} onClick={() => { setEstilo(m.id); setMenuEstilo(m.id) }}
                className={`text-left p-3 rounded-xl border-2 transition ${
                  estilo === m.id ? 'border-brand-500 bg-brand-50' : 'border-ink-200 hover:border-ink-300 bg-white'
                }`}>
                <Mini id={m.id} />
                <div className="flex items-center gap-2 mt-2">
                  <span className="font-semibold text-sm text-ink-900">{m.nome}</span>
                  {m.id === 'padrao' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-ink-100 text-ink-600">padrão</span>}
                  {estilo === m.id && <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-600 text-white ml-auto">ativo</span>}
                </div>
                <div className="text-[11px] text-ink-500 mt-0.5">{m.desc}</div>
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-ink-700 mb-2">Estilo do dashboard</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {DASH_ESTILOS.map(m => (
              <button key={m.id} onClick={() => { setDash(m.id); setDashEstilo(m.id) }}
                className={`text-left p-3 rounded-xl border-2 transition ${
                  dash === m.id ? 'border-brand-500 bg-brand-50' : 'border-ink-200 hover:border-ink-300 bg-white'
                }`}>
                <DashMini id={m.id} />
                <div className="flex items-center gap-2 mt-2">
                  <span className="font-semibold text-sm text-ink-900">{m.nome}</span>
                  {m.id === 'padrao' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-ink-100 text-ink-600">padrão</span>}
                  {dash === m.id && <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-600 text-white ml-auto">ativo</span>}
                </div>
                <div className="text-[11px] text-ink-500 mt-0.5">{m.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="text-sm font-medium text-ink-700 mb-2">Zoom da interface</div>
          <div className="flex items-center gap-3">
            <input type="range" min={70} max={130} step={10} value={zoom}
              onChange={e => { const v = Number(e.target.value); setZoomState(v); persistZoom(v) }}
              className="w-64 max-w-full" />
            <span className="font-mono text-sm text-ink-700 w-12">{zoom}%</span>
            <button onClick={() => { setZoomState(100); persistZoom(100) }}
              className="text-xs px-2 py-1 rounded border border-ink-200 hover:bg-ink-50">Resetar</button>
          </div>
          <p className="text-[11px] text-ink-500 mt-1">Diminua pra caber mais em monitores pequenos. Também dá pra ajustar pelo − / + no topo.</p>
        </div>
      </div>
    </div>
  )
}

export function ConfiguracoesPage() {
  const { data: usuarios = [] } = useQuery<any[]>({
    queryKey: ['usuarios'],
    queryFn: async () => (await api.get('/auth/usuarios')).data,
  })
  return (
    <div className="space-y-4">
      <AparenciaCard />
      <WhatsAppCard />
      <div className="card">
        <div className="px-5 py-4 border-b border-ink-200"><h3 className="font-semibold">Usuários do Sistema</h3></div>
        <div className="table-wrap !rounded-t-none !border-t-0">
          <table className="table">
            <thead><tr><th>Nome</th><th>E-mail</th><th>Perfil</th><th>Status</th></tr></thead>
            <tbody>
              {usuarios.map((u: any) => (
                <tr key={u.id}>
                  <td className="font-medium">{u.nome}</td>
                  <td className="text-ink-600">{u.email}</td>
                  <td className="capitalize">{u.perfil}</td>
                  <td>{u.ativo ? <span className="badge-success">Ativo</span> : <span className="badge-neutral">Inativo</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
